import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Zentrale „Zurück"-Navigation für die eingeloggte Anwendung.
 *
 * Verhalten:
 *  * Auf dem Dashboard (`/`) selbst wird **nichts** gerendert — man
 *    kann von der Startseite nicht sinnvoll „zurück" gehen.
 *  * Ist eine echte interne History vorhanden (`history.state.idx > 0`),
 *    springen wir einen Eintrag zurück (react-router setzt einen
 *    steigenden `idx` beim internen Navigieren; ein Direktaufruf startet
 *    bei 0).
 *  * Sonst Fallback → `/` (Dashboard). Dadurch endet man nie im
 *    weißen Nichts, wenn eine Seite direkt per URL geöffnet wurde.
 *
 * Bewusst NICHT auf öffentlichen Auth-Seiten (Login, Register, Reset,
 * Verify) und nicht im Kundenportal integriert — dort gelten eigene
 * Navigationskontrakte.
 */
export default function BackButton({ className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname === "/") return null;

  const canGoBack =
    typeof window !== "undefined" &&
    window.history?.state &&
    typeof window.history.state.idx === "number" &&
    window.history.state.idx > 0;

  const goBack = () => {
    if (canGoBack) navigate(-1);
    else navigate("/", { replace: false });
  };

  return (
    <Button
      type="button"
      data-testid="back-btn"
      variant="ghost"
      size="sm"
      onClick={goBack}
      aria-label="Zurück"
      className={
        "h-8 -ml-2 px-2 text-zinc-400 hover:text-cyan-400 " +
        "uppercase tracking-widest text-[11px] " +
        className
      }
    >
      <ChevronLeft size={16} className="mr-1" />
      <span>Zurück</span>
    </Button>
  );
}
